import React from 'react';
import { connect } from 'react-redux';
import Logger, { LOG_LEVELS } from '../../../utils/logger';
import ReactTable from "react-table";
import LanguageStringResolver from '../../fields/language/LanguageStringResolver';
import Pagination from '../../page/Pagination'
import { withFullStoryExclude } from '../../../utils/fullStoryHelper';
import ModifyPopup from './ModifyPopup';
import { ECTypeEnums } from '../../../utils/enums';
import VATImportSection from './VATImportSection';
import { isECListingImportEnabled, EC_TYPE_TEMPLATE_MAPPING } from '../../../utils/ecListingImportConfig';


const log = new Logger('LISTING_LINES', LOG_LEVELS.DEBUG);
class ListingLines extends React.Component {

    constructor(props) {
        super(props);
        this.onECTypeChange = this.onECTypeChange.bind(this);
        this.onRenderEditable = this.onRenderEditable.bind(this);
        this.handleVATImportData = this.handleVATImportData.bind(this);
        this.toggle = this.toggle.bind(this);
        this.toggleCallOfStocks = this.toggleCallOfStocks.bind(this);
        this.state = {
            sales: true,
            showCallOfStocks: false, // New state for Call of Stocks
            modifyPopupVisible: false,
            selectedRow: null
        };
    }

    /**
     * Handles imported VAT data from the VATImportSection.
     * Clears existing sales data and replaces with imported data.
     * 
     * @param {Array} importedLines - Array of line items from the imported file
     */
    handleVATImportData(importedLines) {
        log.debug('handleVATImportData: Received lines', importedLines.length);
        
        if (typeof this.props.onVATImport === 'function') {
            this.props.onVATImport(importedLines);
        }
    }

    // Adding a new methods for handling modification
    handleModify = (row, original, index) => {
        this.setState({
            modifyPopupVisible: true,
            selectedRow: { ...original, index }
        });
    };

    handleSaveModification = (modifiedData) => {
        const { selectedRow } = this.state;
        const { index } = selectedRow;


        // Calling the new prop function to handle the save
        if (this.state.showCallOfStocks) {
            this.props.onModifyCallOfStocks(modifiedData, index, ECTypeEnums.CALLOFSTOCKS);
        } else if (this.state.sales) {
            this.props.onModifySales(modifiedData, index, ECTypeEnums.SALES);
        }

        this.setState({
            modifyPopupVisible: false,
            selectedRow: null
        });
    };

    handleCancelModification = () => {
        this.setState({
            modifyPopupVisible: false,
            selectedRow: null
        });
    };

    toggle() {
        let sales = this.state.sales;
        this.setState({
            sales: !sales,
            showCallOfStocks: false
        });
        this.props.onListingTypeChange(!sales);
    }

    toggleCallOfStocks() {
        this.setState({
            showCallOfStocks: !this.state.showCallOfStocks,
            sales: false     // When showing Call of Stocks, we hide sales
        });
        this.props.onListingTypeChange(false);
    }

    /**    
    * Runs when the delete button is clicked and deletes the row associated with the clicked delete button. 
    *
    * @param {*} row Original row from your data
    * @param {*} original The post-accessed values from the original row
    * @param {*} index The index of the row
    * @returns
    * @memberof ListingLines
    */
    onDelete(row, original, index) {
        this.props.onDelete(row, original, index, this.state.sales, this.props.filingTypeId);
    }

    onECTypeChange(newValue, row, original, index) {
        this.props.onECTypeChange(newValue, row, original, index, this.state.sales);
    }

    onRenderEditable(cellInfo) {
        return this.props.renderEditable(cellInfo, this.state.sales);
    }

    getECTypeLabel(row, original) {
        if (!original.ECTypes || original.ECTypes.length === 0) {
            const ecTypeNum = Number.parseInt(row.ECType, 10);
            return EC_TYPE_TEMPLATE_MAPPING[ecTypeNum]?.label || row.ECType;
        }
        const types = original.ECTypes.filter(ECType => ECType.value === row.ECType);
        return types.length > 0 ? types[0].label : row.ECType;
    }

    formatDate(value) {
        return value ? new Date(value).toLocaleDateString() : '';
    }

    getCurrentViewLabel() {
        if (this.state.sales) return 'listingLines_Sales';
        if (this.state.showCallOfStocks) return 'listingLines_CallOfStocks';
        return 'listingLines_Acquisitions';
    }

    getToggleLabel() {
        return this.state.sales ? 'listingLines_SwitchToAcquisitions' : 'listingLines_SwitchToSales';
    }

    getCallOfStocksToggleLabel() {
        return this.state.showCallOfStocks ? 'listingLines_SwitchToAcquisitions' : 'listingLines_SwitchToCallOfStocks';
    }

    createTable(data, columns) {
        return (
            <s-col span="12">
                <ReactTable
                    data={data}
                    columns={columns}
                    defaultPageSize={10}
                    minRows="0"
                    showPageJump={false}
                    showPagination={true}
                    PaginationComponent={Pagination}
                />
            </s-col>
        );
    }

    renderECTypeCell = ({ row, original }) => {
        return <span>{this.getECTypeLabel(row, original)}</span>;
    }

    renderDateCell = ({ value }) => {
        return this.formatDate(value);
    }

    // Reusable cell renderers
    renderModifyCell = ({ row, original, index }) => {
        return (
            <button
                className="tertiary"
                onClick={() => this.handleModify(row, original, index)}
            >
                <LanguageStringResolver string='listingLines_Modify' />
            </button>
        );
    }

    renderDeleteCellSales = ({ row, original, index }) => {
        return (
            <button 
                className="tertiary" 
                onClick={() => this.props.onDelete(row, original, index, true, ECTypeEnums.SALES, this.props.filingTypeId)}
            >
                <LanguageStringResolver string='listingLines_Delete' />
            </button>
        );
    }

    renderDeleteCellPurchase = ({ row, original, index }) => {
        return (
            <button 
                className="tertiary" 
                onClick={() => this.props.onDelete(row, original, index, false, ECTypeEnums.PURCHASES, this.props.filingTypeId)}
            >
                <LanguageStringResolver string='listingLines_Delete' />
            </button>
        );
    }

    renderDeleteCellCallOfStocks = ({ row, original, index }) => {
        return (
            <button 
                className="tertiary" 
                onClick={() => this.props.onDelete(row, original, index, false, ECTypeEnums.CALLOFSTOCKS, this.props.filingTypeId)}
            >
                <LanguageStringResolver string='listingLines_Delete' />
            </button>
        );
    }

    // Reusable header renderers
    renderVATNumberHeader = () => <LanguageStringResolver string='listingLines_VATNumberSupplier' />
    renderValueHeader = () => <LanguageStringResolver string='listingLines_Value' />
    renderECTypeHeader = () => <LanguageStringResolver string='listingLines_ECType' />
    renderModifyHeader = () => <LanguageStringResolver string='listingLines_Modify' />
    renderDeleteHeader = () => <LanguageStringResolver string='listingLines_Delete' />
    renderCoSVatNumberHeader = () => <LanguageStringResolver string='listingLines_coSvatNumber' />
    renderCoSNameHeader = () => <LanguageStringResolver string='listingLines_coSname' />
    renderCoSListingTypeHeader = () => <LanguageStringResolver string='listingLines_coSlistingTypeAsString' />
    renderCoSDateHeader = () => <LanguageStringResolver string='listingLines_coSDate' />
    renderCoSAmountHeader = () => <LanguageStringResolver string='listingLines_coSamount' />
    renderCoSOriginalBuyerVRNHeader = () => <LanguageStringResolver string='listingLines_coSOriginalBuyerVRN' />
    renderCoSOriginalBuyerNameHeader = () => <LanguageStringResolver string='listingLines_coSOriginalBuyerName' />

    getModifyColumn() {
        return {
            id: "modify",
            Header: this.renderModifyHeader,
            Cell: this.renderModifyCell,
            width: 100,
            sortable: false
        };
    }

    getDeleteColumnSales() {
        return {
            id: "checkbox",
            accessor: "SendingEnabled",
            Header: this.renderDeleteHeader,
            Cell: this.renderDeleteCellSales,
            className: 'text-align-center',
            width: 100,
            sortable: false
        };
    }

    getDeleteColumnPurchase() {
        return {
            id: "delete",
            Header: this.renderDeleteHeader,
            Cell: this.renderDeleteCellPurchase,
            className: 'text-align-center',
            width: 100,
            sortable: false
        };
    }

    getDeleteColumnCallOfStocks() {
        return {
            id: "delete",
            Header: this.renderDeleteHeader,
            Cell: this.renderDeleteCellCallOfStocks,
            className: 'text-align-center',
            width: 100,
            sortable: false
        };
    }

    getSalesColumns() {
        let columns =  [{
            Header: this.renderVATNumberHeader,
            accessor: 'VATNumberSupplier',
            filterable: true
        }, {
            Header: this.renderValueHeader,
            accessor: 'Value',
            filterable: true,
            headerClassName: withFullStoryExclude(),
            className: withFullStoryExclude()
        }, {
            Header: this.renderECTypeHeader,
            accessor: 'ECType',
            filterable: false,
            sortable: false,
            Cell: this.renderECTypeCell
        }, 
        this.getDeleteColumnSales()];

        if (this.props.enableEClistingModification) {
            columns.splice(-1, 0, this.getModifyColumn());
        }
                
        return columns;
    }

    getPurchaseColumns() {
        let columns =  [{
            Header: this.renderVATNumberHeader,
            accessor: 'VATNumberSupplier',
            filterable: true
        }, {
            Header: this.renderValueHeader,
            accessor: 'Value',
            filterable: true,
            headerClassName: withFullStoryExclude(),
            className: withFullStoryExclude()
        }, {
            Header: this.renderECTypeHeader,
            accessor: 'ECType',
            filterable: false,
            sortable: false,
            Cell: this.renderECTypeCell
        }, 
        this.getDeleteColumnPurchase()];

        if (this.props.enableEClistingModification) {
            columns.splice(-1, 0, this.getModifyColumn());
        }
                
        return columns;
    }

    getCallOfStocksColumns() {
        let columns= [{
            Header: this.renderCoSVatNumberHeader,
            accessor: 'coSvatNumber',
            filterable: true
        }, {
            Header: this.renderCoSNameHeader,
            accessor: 'coSname',
            filterable: true
        }, {
            Header: this.renderCoSListingTypeHeader,
            accessor: 'coSlistingTypeAsString',
            filterable: true
        }, {
            Header: this.renderCoSDateHeader,
            accessor: 'coSDate',
            filterable: true,
            Cell: this.renderDateCell
        }, {
            Header: this.renderCoSAmountHeader,
            accessor: 'coSamount',
            filterable: true,
            headerClassName: withFullStoryExclude(),
            className: withFullStoryExclude()
        }, {
            Header: this.renderCoSOriginalBuyerVRNHeader,
            accessor: 'coSOriginalBuyerVRN',
            filterable: true
        }, {
            Header: this.renderCoSOriginalBuyerNameHeader,
            accessor: 'coSOriginalBuyerName',
            filterable: true
        }];

        if (this.props.enableEClistingModification) {
            columns.push(this.getModifyColumn(), this.getDeleteColumnCallOfStocks());
        } 
        return columns;
    }

    renderTable() {
        if (this.state.sales) {
            return this.createTable(this.props.salesData, this.getSalesColumns());
        }

        if (this.state.showCallOfStocks) {
            return this.createTable(this.props.callOfStocksData, this.getCallOfStocksColumns());
        }

        return this.createTable(this.props.purchaseData, this.getPurchaseColumns());
    }

    render() {
        // Check if VAT Import section should be shown
        const showVATImportSection = isECListingImportEnabled(
            this.props.countryCode,
            this.props.featureFlags
        ) && this.state.sales;

        return (
            <div data-testid="listing-line-item">
                <h2 className="mt15" style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span>EC</span>
                        <LanguageStringResolver string={this.getCurrentViewLabel()} />
                        {'  '}
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: '10px',
                        gap: '20px'
                    }}>
                        <a
                            className="tertiary small"
                            onClick={this.toggle}
                        >
                            <LanguageStringResolver string={this.getToggleLabel()} />
                        </a>

                    
                        {(this.props.enableCoSGrid) ? <a
                            className="tertiary small"
                            onClick={this.toggleCallOfStocks}
                        >
                            <LanguageStringResolver string={this.getCallOfStocksToggleLabel()} />
                        </a> : null}
                    </div>
                </h2>
                <s-row>
                 {this.renderTable()}
                </s-row>
                {this.state.modifyPopupVisible && (
                    <ModifyPopup
                        data={this.state.selectedRow}
                        onSave={this.handleSaveModification}
                        onCancel={this.handleCancelModification}
                        isCallOfStocks={this.state.showCallOfStocks}
                    />
                )}

                {/* VAT Import Section - Only shown for supported countries */}
                {showVATImportSection && (
                    <VATImportSection onDataImported={this.handleVATImportData} />
                )}
            </div>
        );
    }
}


ListingLines.defaultProps = {
    onDelete: () => { },
    onListingTypeChange: () => { },
    onModifySales: () => { },
    onModifyCallOfStocks: () => { },
    countryCode: '',
    onVATImport: () => { },
    featureFlags: {},
    enableEClistingModification: false,
    enableCoSGrid: false,
    renderEditable: () => null
};

function mapStateToProps(state) {
    log.debug('mapStateToProps: ', state);
    return {
        languages: state.languages,
        enableCoSGrid: state.featureFlag?.data?.EnableCoSGrid,
        enableEClistingModification: state.featureFlag?.data?.EnableEClistingModification,
        featureFlags: state.featureFlag?.data || {}
    };
}


export default connect(mapStateToProps)(ListingLines);
